import { useSite } from "@inrange/building-manager-api-client";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";

import {
  RecursivePartialSiteAllowStringValues,
  Site,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import {
  buildSiteCalculationPayload,
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { BorderedCheckbox, Tooltip } from "@inrange/theme-components";
import { currencySymbol } from "@inrange/theme-components/formatting";
import { useState } from "react";
import { Alert, Dropdown } from "react-bootstrap";
import { useReactQueryError } from "../../../utils/react-query-error";
import FormInputPercentage from "../../FormInputPercentage";
import CollapsibleCard from "../CollapsibleCard";
import {
  currencyValue,
  formatNumber2dp,
  parseAsNumber,
  roundToDp,
} from "./utils";

const FinancialPerformanceInRange = ({
  site,
  setSite,
  isOwnerOccupied,
  isBulkEdit,
  bulkEditSiteIds,
  bulkEditSpvReportPrefixName,
}: {
  site: SiteAllowStringValues;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  isOwnerOccupied: boolean;
  isBulkEdit?: boolean;
  bulkEditSiteIds?: Set<string>;
  bulkEditSpvReportPrefixName?: string;
}) => {
  const [isReportDebug, setIsReportDebug] = useState(false);
  const { fetchSiteReportPreview, fetchSitesReport } = useSite({
    app: "admin",
  });

  useReactQueryError(fetchSiteReportPreview, "Error downloading report");

  useReactQueryError(fetchSitesReport, "Error downloading report");

  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const ownershipKey = isOwnerOccupied ? "ownerOccupier" : "landlord";

  const fmIR = siteCalculations.financialModelsInrange;
  const irPaybackYears = fmIR?.lease.paybackMonths
    ? fmIR.lease.paybackMonths / 12
    : 0;

  const fieldWidth = isBulkEdit ? 3 : 4;

  const meetingLoanTermsFields = (
    <>
      <Col sm={fieldWidth}>
        <Form.Group className="mb-3" controlId="dscrMin">
          <Form.Label>DSCR min achieved</Form.Label>
          <Form.Control
            disabled
            value={
              fmIR?.lease?.dscrMin ? roundToDp(fmIR.lease.dscrMin, 4) : "N/A"
            }
            isInvalid={
              !!fmIR?.lease?.dscrMin &&
              fmIR?.lease?.dscrMin <
                parseAsNumber(site.costInputsInrange!.targetDscrMin)
            }
          />
        </Form.Group>
      </Col>
      <Col sm={fieldWidth}>
        <Form.Group className="mb-3" controlId="dscrAvg">
          <Form.Label>DSCR avg achieved</Form.Label>
          <Form.Control
            disabled
            value={
              fmIR?.lease?.dscrAvg ? roundToDp(fmIR.lease.dscrAvg, 4) : "N/A"
            }
            isInvalid={
              !!fmIR?.lease?.dscrAvg &&
              fmIR?.lease?.dscrAvg <
                parseAsNumber(site.costInputsInrange!.targetDscrAvg)
            }
          />
        </Form.Group>
      </Col>
      <Col sm={fieldWidth}>
        <Form.Group className="mb-3" controlId="llcrMin">
          <Form.Label>LLCR min achieved</Form.Label>
          <Form.Control
            disabled
            value={
              fmIR?.lease?.llcrMin ? roundToDp(fmIR.lease.llcrMin, 4) : "N/A"
            }
            isInvalid={
              !!fmIR?.lease?.llcrMin &&
              fmIR?.lease?.llcrMin <
                parseAsNumber(site.costInputsInrange!.targetLlcrMin)
            }
          />
        </Form.Group>
      </Col>
    </>
  );

  const showLeaseFields =
    site.investmentModel === "lease" || site.investmentModel === "none";
  const showLicenseFields =
    site.investmentModel === "license" || site.investmentModel === "none";

  return (
    <CollapsibleCard
      id="financial-performance-inrange"
      title={"InRange financial performance"}
      className="mt-2"
      isBulkEdit={isBulkEdit}
      menu={
        showLeaseFields || isBulkEdit ? (
          <Dropdown>
            <Dropdown.Toggle
              variant="secondary"
              id="dropdown-basic"
              style={{ paddingTop: "2px", paddingBottom: "2px" }}
            >
              Downloads
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  if (isBulkEdit) {
                    fetchSitesReport.mutate({
                      siteIds: Array.from(bulkEditSiteIds || []),
                      payload: {
                        costInputsInrange: site.costInputsInrange,
                      },
                      prefixName:
                        bulkEditSpvReportPrefixName || "multi-site-spv",
                      reportName: "inrange_spv",
                      fileName: "spv-cashflow",
                    });
                  } else {
                    fetchSiteReportPreview.mutate({
                      reportName: "inrange_spv",
                      fileName: "spv-cashflow",
                      siteName: site.name,
                      payload: {
                        site: buildSiteCalculationPayload({
                          site: site as unknown as Partial<Site>,
                          clearNonCalcFields: false,
                        }),
                      },
                      options: {
                        ...(isReportDebug && { debug: true }),
                      },
                    });
                  }
                }}
              >
                InRange SPV cashflow (P90, Lease)
                {!isBulkEdit && site.pvForecastProbability !== "P90" && (
                  <>
                    <br />
                    ℹ️ Warning: Numbers in report will differ from <br />
                    on-screen as download always uses P90 generation.
                  </>
                )}
              </Dropdown.Item>
              <Dropdown.Item
                as="div"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsReportDebug(!isReportDebug);
                }}
              >
                <Form.Check
                  type="switch"
                  label="Enable debug data in reports"
                  checked={isReportDebug}
                  readOnly
                />
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ) : undefined
      }
    >
      {!isBulkEdit && (
        <>
          {site.pvForecastProbability !== "P90" && (
            <Alert variant="warning">
              Warning: These figures are based on {site.pvForecastProbability}{" "}
              generation.
            </Alert>
          )}
          <Row className="d-flex flex-wrap align-items-end">
            <Col sm={fieldWidth}>
              <Form.Group className="mb-3" controlId="grossEnergyRevenue">
                <Form.Label>Gross revenue (Y1)</Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    fmIR?.defaultRevenue.grossEnergyRevenue || 0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={fieldWidth}>
              <Form.Group className="mb-3" controlId="grossEnergyRevenueOnsite">
                <Form.Label>
                  On-site gross rev (Y1){" "}
                  <Tooltip
                    position="left"
                    text="On-site direct from PV + On-site from battery discharge"
                  />
                </Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    fmIR?.defaultRevenue.grossEnergyRevenueOnsite || 0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={fieldWidth}>
              <Form.Group className="mb-3" controlId="grossEnergyRevenueExport">
                <Form.Label>Export gross rev (Y1)</Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    fmIR?.defaultRevenue.grossEnergyRevenueExport || 0,
                    siteCalculations.currencyCode
                  )}
                  data-testid="exportGrossRevenueY1"
                />
              </Form.Group>
            </Col>
            {isOwnerOccupied && showLicenseFields && (
              <>
                <Col sm={fieldWidth}>
                  <Form.Group className="mb-3" controlId="grossEnergyRevenue">
                    <Form.Label>License OO Gross revenue (Y1)</Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        fmIR?.ooLicenseRevenue.grossEnergyRevenue || 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={fieldWidth}>
                  <Form.Group
                    className="mb-3"
                    controlId="grossEnergyRevenueOnsite"
                  >
                    <Form.Label>License OO On-site gross rev (Y1)</Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        fmIR?.ooLicenseRevenue.grossEnergyRevenueOnsite || 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={fieldWidth}>
                  <Form.Group
                    className="mb-3"
                    controlId="grossEnergyRevenueExport"
                  >
                    <Form.Label>License OO Export gross rev (Y1)</Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        fmIR?.ooLicenseRevenue.grossEnergyRevenueExport || 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
              </>
            )}
            {(siteCalculations.financialModelsInrange?.tariffArbitrageRevenue
              .grossProcurementValue || 0) > 0 && (
              <Col sm={fieldWidth}>
                <Form.Group className="mb-3" controlId="grossRevenue">
                  <Form.Label>Energy arb. gross rev (Y1)</Form.Label>
                  <Form.Control
                    disabled
                    value={currencyValue(
                      fmIR?.tariffArbitrageRevenue.grossRevenue || 0,
                      siteCalculations.currencyCode
                    )}
                  />
                </Form.Group>
              </Col>
            )}
            {showLeaseFields && (
              <Col sm={fieldWidth}>
                <Form.Group className="mb-3" controlId="irNetRevenueY2Lease">
                  <Form.Label
                    style={
                      site.costInputsInrange!.leaseNetArrOverride != null &&
                      site.costInputsInrange!.leaseNetArrOverride !== ""
                        ? {
                            textDecoration: "line-through",
                            textDecorationThickness: "2px",
                          }
                        : undefined
                    }
                  >
                    IR net ARR (Y2, Lease)
                  </Form.Label>
                  <Form.Control
                    disabled
                    value={currencyValue(
                      fmIR?.lease?.netRevenue || 0,
                      siteCalculations.currencyCode
                    )}
                    isInvalid={(fmIR?.lease?.netRevenue ?? 0) <= 0}
                    style={
                      site.costInputsInrange!.leaseNetArrOverride != null &&
                      site.costInputsInrange!.leaseNetArrOverride !== ""
                        ? {
                            textDecoration: "line-through",
                            textDecorationThickness: "2px",
                          }
                        : undefined
                    }
                  />
                </Form.Group>
              </Col>
            )}
            {site.investmentModel === "lease" && (
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.leaseNetArrOverride"
                >
                  <Form.Label>
                    Lease ARR override (
                    {currencySymbol(siteCalculations.currencyCode)})
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.leaseNetArrOverride ?? ""}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          leaseNetArrOverride: e.target.value,
                        },
                      })
                    }
                    isInvalid={
                      !!errors["costInputsInrange.leaseNetArrOverride"]
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.leaseNetArrOverride"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
            {showLicenseFields && (
              <Col sm={fieldWidth}>
                <Form.Group className="mb-3" controlId="irNetRevenueY1License">
                  <Form.Label>IR net ARR (Y1, License) </Form.Label>
                  <Form.Control
                    disabled
                    value={currencyValue(
                      siteCalculations.financialModels?.[ownershipKey]?.license
                        ?.inrangeNetRevenue || 0,
                      siteCalculations.currencyCode
                    )}
                    isInvalid={
                      (siteCalculations.financialModels?.[ownershipKey]?.license
                        ?.inrangeNetRevenue ?? 0) <= 0
                    }
                  />
                </Form.Group>
              </Col>
            )}
            <Col sm={fieldWidth}>
              <Form.Group className="mb-3" controlId="totalCostToInRange">
                <Form.Label>
                  Upfront InRange cost (
                  {currencySymbol(siteCalculations.currencyCode)}){" "}
                  <Tooltip
                    position="left"
                    text="EPC-only + free-issue + InRange battery hardware & install"
                  />
                </Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    siteCalculations.projectCosts?.totalCostToInRange || 0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={fieldWidth}>
              <Form.Group className="mb-3" controlId="irNetRevenueY1Lease">
                <Form.Label
                  style={
                    site.costInputsInrange!.arbitrageOverride != null &&
                    site.costInputsInrange!.arbitrageOverride !== ""
                      ? {
                          textDecoration: "line-through",
                          textDecorationThickness: "2px",
                        }
                      : undefined
                  }
                >
                  Upfront arbitrage{" "}
                  {showLeaseFields && (
                    <Tooltip
                      position="left"
                      text="On a lease site this total is after subtracting the 'Total equity injection'"
                    />
                  )}
                </Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    fmIR?.installArbitrage || 0,
                    siteCalculations.currencyCode
                  )}
                  style={
                    site.costInputsInrange!.arbitrageOverride != null &&
                    site.costInputsInrange!.arbitrageOverride !== ""
                      ? {
                          textDecoration: "line-through",
                          textDecorationThickness: "2px",
                        }
                      : undefined
                  }
                />
              </Form.Group>
            </Col>
            <Col sm={fieldWidth}>
              <Form.Group
                className="mb-3"
                controlId="costInputsInrange.arbitrageOverride"
              >
                <Form.Label>
                  Arbitrage override (
                  {currencySymbol(siteCalculations.currencyCode)})
                </Form.Label>
                <Form.Control
                  type="text"
                  value={site.costInputsInrange!.arbitrageOverride ?? ""}
                  onChange={(e) =>
                    setSite({
                      costInputsInrange: {
                        arbitrageOverride: e.target.value,
                      },
                    })
                  }
                  isInvalid={!!errors["costInputsInrange.arbitrageOverride"]}
                />
                <Form.Control.Feedback type="invalid">
                  {errors["costInputsInrange.arbitrageOverride"]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {showLeaseFields && (
              <>
                <Col sm={fieldWidth}>
                  <Form.Group className="mb-3" controlId="equityInjected">
                    <Form.Label>
                      Total equity injection (
                      {currencySymbol(siteCalculations.currencyCode)}){" "}
                      <Tooltip
                        position="left"
                        text="Equity injection covers a % of CapEx, VAT, Legal/Modeling fees, plus all of the arrangement fee, commitment fee, and first monitoring fee"
                      />
                    </Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        fmIR?.equityInjected || 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={fieldWidth}>
                  <Form.Group className="mb-3" controlId="irIrrLease">
                    <Form.Label>InRange IRR (Lease, %)</Form.Label>
                    <Form.Control
                      disabled
                      value={
                        fmIR?.lease?.irr !== undefined
                          ? formatNumber2dp(fmIR?.lease?.irr * 100)
                          : "N/A"
                      }
                    />
                  </Form.Group>
                </Col>
                <Col sm={fieldWidth}>
                  <Form.Group className="mb-3" controlId="irDebtPayback">
                    <Form.Label>Debt payback (years)</Form.Label>
                    <Form.Control
                      disabled
                      value={
                        fmIR?.lease?.paybackMonths
                          ? roundToDp(irPaybackYears, 2)
                          : "N/A"
                      }
                      isInvalid={
                        irPaybackYears >
                        parseAsNumber(site.costInputsInrange!.loanTermYears)
                      }
                    />
                  </Form.Group>
                </Col>
              </>
            )}
            {site.tariffConfig?.takeOrPay &&
              (fmIR?.takeOrPayNonMonetisedExportKwh || 0) > 0 && (
                <Col sm={fieldWidth}>
                  <Form.Group
                    className="mb-3"
                    controlId="takeOrPayNonMonetisedExportKwh"
                  >
                    <Form.Label>
                      ToP non-monetised export (Y1, kWh){" "}
                      <Tooltip
                        position="left"
                        text="Export kWh not monetised due to take-or-pay commitment"
                      />
                    </Form.Label>
                    <Form.Control
                      disabled
                      value={formatNumber2dp(
                        fmIR?.takeOrPayNonMonetisedExportKwh || 0
                      )}
                    />
                  </Form.Group>
                </Col>
              )}
            {!isBulkEdit &&
              site.investmentModel === "lease" &&
              meetingLoanTermsFields}
          </Row>
        </>
      )}
      {(isBulkEdit || showLeaseFields) && (
        <CollapsibleCard
          title={"Loan terms"}
          className="mt-2"
          initialOpen={!!isBulkEdit}
        >
          <>
            <Row className="d-flex flex-wrap align-items-end">
              {!isBulkEdit &&
                site.investmentModel !== "lease" &&
                meetingLoanTermsFields}
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.constructionMonths"
                >
                  <Form.Label>Const. period (months)</Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.constructionMonths}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          constructionMonths: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.constructionMonths"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.constructionMonths"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.targetDscr"
                >
                  <Form.Label>DSCR target</Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.targetDscr}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          targetDscr: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.targetDscr"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.targetDscr"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.debtPaybackDividendRate"
                >
                  <Form.Label>Debt payback dividend (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.debtPaybackDividendRate}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          debtPaybackDividendRate: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.debtPaybackDividendRate`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Label>Interest payment shaping</Form.Label>
                <Form
                  style={{
                    display: "flex",
                    fontSize: 12,
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <BorderedCheckbox
                    formName={"shapeInterestPayments"}
                    label={"True"}
                    value={"true"}
                    selectedOption={(
                      site.costInputsInrange!.shapeInterestPayments ?? true
                    ).toString()}
                    setSelectedOption={(value) =>
                      setSite({
                        costInputsInrange: {
                          shapeInterestPayments: value === "true",
                        },
                      })
                    }
                  />
                  <BorderedCheckbox
                    dataTestId="shapeInterestPaymentsFalse"
                    formName={"shapeInterestPayments"}
                    label={"False"}
                    value={"false"}
                    selectedOption={(
                      site.costInputsInrange!.shapeInterestPayments ?? true
                    ).toString()}
                    setSelectedOption={(value) =>
                      setSite({
                        costInputsInrange: {
                          shapeInterestPayments: value === "true",
                        },
                      })
                    }
                  />
                </Form>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Label>Include all costs</Form.Label>
                <Form
                  style={{
                    display: "flex",
                    fontSize: 12,
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <BorderedCheckbox
                    formName={"includeAllCosts"}
                    label={"True"}
                    value={"true"}
                    selectedOption={(
                      site.costInputsInrange!.includeAllCosts ?? false
                    ).toString()}
                    setSelectedOption={(value) =>
                      setSite({
                        costInputsInrange: {
                          includeAllCosts: value === "true",
                        },
                      })
                    }
                  />
                  <BorderedCheckbox
                    formName={"includeAllCosts"}
                    label={"False"}
                    value={"false"}
                    selectedOption={(
                      site.costInputsInrange!.includeAllCosts ?? false
                    ).toString()}
                    setSelectedOption={(value) =>
                      setSite({
                        costInputsInrange: {
                          includeAllCosts: value === "true",
                        },
                      })
                    }
                  />
                </Form>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.equityFraction"
                >
                  <Form.Label>InRange equity (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.equityFraction}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          equityFraction: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.equityFraction`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.targetDscrMin"
                >
                  <Form.Label>DSCR min</Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.targetDscrMin}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          targetDscrMin: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.targetDscrMin"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.targetDscrMin"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.targetDscrAvg"
                >
                  <Form.Label>DSCR avg</Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.targetDscrAvg}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          targetDscrAvg: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.targetDscrAvg"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.targetDscrAvg"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.discountRate"
                >
                  <Form.Label>Discount rate (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.discountRate}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          discountRate: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.discountRate`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.targetLlcrMin"
                >
                  <Form.Label>LLCR min</Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.targetLlcrMin}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          targetLlcrMin: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.targetLlcrMin"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.targetLlcrMin"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.interestRateConstruction"
                >
                  <Form.Label>Interest constr (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.interestRateConstruction}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          interestRateConstruction: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.interestRateConstruction`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.interestRateOperationalFixed"
                >
                  <Form.Label>Fixed interest oper (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.interestRateOperationalFixed}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          interestRateOperationalFixed: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.interestRateOperationalFixed`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.interestRateOperationalFixedTermYears"
                >
                  <Form.Label>Fixed interest oper term (years)</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      site.costInputsInrange!
                        .interestRateOperationalFixedTermYears
                    }
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          interestRateOperationalFixedTermYears: e.target.value,
                        },
                      })
                    }
                    isInvalid={
                      !!errors[
                        "costInputsInrange.interestRateOperationalFixedTermYears"
                      ]
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {
                      errors[
                        "costInputsInrange.interestRateOperationalFixedTermYears"
                      ]
                    }
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.interestRateOperationalVariable"
                >
                  <Form.Label>Variable interest oper (%)</Form.Label>
                  <FormInputPercentage
                    value={
                      site.costInputsInrange!.interestRateOperationalVariable
                    }
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          interestRateOperationalVariable: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.interestRateOperationalVariable`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.loanTermYears"
                >
                  <Form.Label>Loan term (years)</Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.loanTermYears}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          loanTermYears: e.target.value,
                        },
                      })
                    }
                    isInvalid={
                      !!errors["costInputsInrange.loanTermYears"] ||
                      (siteCalculations.hasAllZeroExportLimit &&
                        parseAsNumber(site.costInputsInrange!.loanTermYears) >
                          10)
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.loanTermYears"] ||
                    siteCalculations.hasAllZeroExportLimit
                      ? `Loan term on zero export site must be 10 years or less`
                      : null}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.commitmentFeeRate"
                >
                  <Form.Label>Commitment fee rate (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.commitmentFeeRate}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          commitmentFeeRate: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.commitmentFeeRate`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.arrangementFeeRate"
                >
                  <Form.Label>Arrangement fee rate (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.arrangementFeeRate}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          arrangementFeeRate: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.arrangementFeeRate`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.annualMonitoringFeeRate"
                >
                  <Form.Label>Annual monitoring fee (%/year)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.annualMonitoringFeeRate}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          annualMonitoringFeeRate: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.annualMonitoringFeeRate`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.vatRate"
                >
                  <Form.Label>VAT rate (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.vatRate}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          vatRate: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.vatRate`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.corporateTaxRate"
                >
                  <Form.Label>Corporation tax rate (%)</Form.Label>
                  <FormInputPercentage
                    value={site.costInputsInrange!.corporateTaxRate}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          corporateTaxRate: e.target.value,
                        },
                      })
                    }
                    errorKey={`costInputsInrange.corporateTaxRate`}
                  />
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.accountingFee"
                >
                  <Form.Label>
                    Accounting fee (
                    {!isBulkEdit && (
                      <>{currencySymbol(siteCalculations.currencyCode)}</>
                    )}
                    /year)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.accountingFee}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          accountingFee: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.accountingFee"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.accountingFee"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.assetMgmtFee"
                >
                  <Form.Label>
                    Asset mgmt fee (
                    {!isBulkEdit && (
                      <>{currencySymbol(siteCalculations.currencyCode)}</>
                    )}
                    /year)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.assetMgmtFee}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          assetMgmtFee: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.assetMgmtFee"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.assetMgmtFee"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.ppaMgmtFee"
                >
                  <Form.Label>
                    PPA mgmt fee (
                    {!isBulkEdit && (
                      <>{currencySymbol(siteCalculations.currencyCode)}</>
                    )}
                    /year)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.ppaMgmtFee}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          ppaMgmtFee: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.ppaMgmtFee"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.ppaMgmtFee"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.legalFees"
                >
                  <Form.Label>
                    Legal fees
                    {!isBulkEdit && (
                      <> ({currencySymbol(siteCalculations.currencyCode)})</>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.legalFees}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          legalFees: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.legalFees"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.legalFees"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={fieldWidth}>
                <Form.Group
                  className="mb-3"
                  controlId="costInputsInrange.modelingFees"
                >
                  <Form.Label>
                    Modeling fees
                    {!isBulkEdit && (
                      <> ({currencySymbol(siteCalculations.currencyCode)})</>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={site.costInputsInrange!.modelingFees}
                    onChange={(e) =>
                      setSite({
                        costInputsInrange: {
                          modelingFees: e.target.value,
                        },
                      })
                    }
                    isInvalid={!!errors["costInputsInrange.modelingFees"]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors["costInputsInrange.modelingFees"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </>
        </CollapsibleCard>
      )}
    </CollapsibleCard>
  );
};

export default FinancialPerformanceInRange;
